import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useRunPhoneCheck } from "@/hooks/agent/useBrandIntegrity";
import { useFullAuditProgress, type AuditPhase } from "@/hooks/agent/useFullAuditProgress";
import { Agent } from "@/types/agent/agent";
import { PhoneCheckKind } from "@/types/agent/brand-integrity";
import { PHONE_CHECK_LABELS } from "@/utils/agent/phoneCheck";
import { PhaseRow } from "./FullAuditSheet";

const SITUATION_COUNT = 5;

interface PhoneCheckCopy {
  question: (genieName: string) => string;
  summary: (genieName: string) => string;
  situations: Array<{ label: string; blurb: string; count: number }>;
  scopeNote: string;
  setupLine: string;
  allRight: string;
  nextAllRight: string;
  nextNeedsLook: string;
}

const COPY: Record<PhoneCheckKind, PhoneCheckCopy> = {
  handoff: {
    question: (genieName) => `Does ${genieName} hand website visitors over to a person at the right moments?`,
    summary: (genieName) =>
      `We write ${SITUATION_COUNT} website-visitor situations around ${genieName}'s own escalation numbers, then play each one against the real Genie. You see whether it decided to hand off. No number is dialled and nobody's phone rings.`,
    situations: [
      { label: "Asks outright", blurb: "\"Can I talk to a person?\" in plain words.", count: 2 },
      { label: "Never says it, clearly needs it", blurb: "A situation only your team can settle.", count: 2 },
      { label: "No handoff wanted", blurb: "An ordinary question the Genie should answer itself.", count: 1 },
    ],
    scopeNote: "This checks the Genie's decision to hand off. It does not test that the phone call itself connects.",
    setupLine: "Reading your Genie's handoff setup",
    allRight: "Hands off at the right moments",
    nextAllRight: "Open the situations to read how each conversation went.",
    nextNeedsLook:
      "Open the situations to see where the Genie's decision differed from what was expected, and read the conversation that led there.",
  },
  transfer: {
    question: (genieName) => `Does ${genieName} put phone callers through to the right number?`,
    summary: (genieName) =>
      `We write ${SITUATION_COUNT} phone-caller situations around ${genieName}'s own transfer numbers, then play each one against the real Genie. You see whether it transferred, and whether it picked the right number. No number is dialled and nobody's phone rings.`,
    situations: [
      { label: "Asks outright", blurb: "\"Put me through to someone\" in plain words.", count: 2 },
      { label: "Never says it, clearly needs it", blurb: "A need that matches one of your transfer numbers.", count: 2 },
      { label: "No transfer wanted", blurb: "An ordinary question the Genie should answer itself.", count: 1 },
    ],
    scopeNote:
      "This checks the Genie's decision to transfer and the number it chose. It does not test that the phone transfer itself connects.",
    setupLine: "Reading your Genie's transfer numbers",
    allRight: "Transfers to the right number every time",
    nextAllRight: "Open the situations to read how each call went.",
    nextNeedsLook:
      "Open the situations to see where the Genie skipped a transfer, transferred when it shouldn't, or picked a different number, and read the call that led there.",
  },
};

const PRIMARY_BUTTON_CLASS =
  "bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg";

interface PhoneCheckSheetProps {
  isOpen: boolean;
  onClose: () => void;
  kind: PhoneCheckKind;
  agent: Agent | null;
  onCheckStarted?: (testRunId: number) => void;
  onViewResults?: (testRunId: number) => void;
}

export const PhoneCheckSheet = ({ isOpen, onClose, kind, agent, onCheckStarted, onViewResults }: PhoneCheckSheetProps) => {
  const runPhoneCheck = useRunPhoneCheck();
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [optimisticPhase, setOptimisticPhase] = useState<AuditPhase>("idle");
  const { state, executionPercent } = useFullAuditProgress(activeRunId);
  const resetMutation = runPhoneCheck.reset;

  // Reset the local run when the sheet closes (the check keeps running in the background).
  useEffect(() => {
    if (!isOpen) {
      setActiveRunId(null);
      setOptimisticPhase("idle");
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  const copy = COPY[kind];
  const title = PHONE_CHECK_LABELS[kind];
  const genieName = agent?.name ?? "your Genie";
  const phase: AuditPhase = activeRunId && state.phase !== "idle" ? state.phase : optimisticPhase;
  const isRunning = phase === "generating" || phase === "generated" || phase === "executing";

  const handleRun = async () => {
    if (!agent?.id) return;
    try {
      setOptimisticPhase("generating");
      const res = await runPhoneCheck.mutateAsync({ agentId: agent.id, kind });
      setActiveRunId(res.testRunId);
      onCheckStarted?.(res.testRunId);
    } catch {
      setOptimisticPhase("idle");
    }
  };

  const handleTryAgain = () => {
    setActiveRunId(null);
    setOptimisticPhase("idle");
    runPhoneCheck.reset();
  };

  const handleViewResults = () => {
    if (activeRunId) onViewResults?.(activeRunId);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl md:max-w-2xl flex flex-col p-0 bg-[#F9FAFC]"
      >
        <VisuallyHidden>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{copy.question(genieName)}</SheetDescription>
        </VisuallyHidden>

        <div className="relative px-6 sm:px-8 pt-6 pb-5 bg-white border-b border-[#E5EAF0]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900 leading-tight">{title}</h2>
              <p className="text-sm text-gray-500 mt-1">{copy.question(genieName)}</p>
            </div>
            <button
              onClick={onClose}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6">
          {phase === "idle" && <PreRun copy={copy} genieName={genieName} error={runPhoneCheck.error?.message} />}
          {isRunning && (
            <InProgress
              copy={copy}
              isWriting={phase === "generating"}
              testsComplete={state.testsComplete}
              totalTests={state.totalTests || SITUATION_COUNT}
              executionPercent={executionPercent}
            />
          )}
          {phase === "completed" && (
            <CompletedState copy={copy} score={state.overallScore} total={state.totalTests || state.testsComplete} />
          )}
          {phase === "failed" && <FailedState message={state.errorMessage} onTryAgain={handleTryAgain} />}
        </div>

        <div className="sticky bottom-0 border-t border-[#E5EAF0] bg-white px-6 sm:px-8 py-4 flex items-center justify-start gap-3">
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleRun} disabled={!agent?.id || runPhoneCheck.isPending} className={PRIMARY_BUTTON_CLASS}>
                Run {title}
              </Button>
            </>
          )}
          {isRunning && (
            <>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <p className="text-xs text-gray-500">The check keeps running if you close this.</p>
            </>
          )}
          {phase === "completed" && (
            <>
              <Button variant="outline" onClick={onClose}>
                Done
              </Button>
              <Button onClick={handleViewResults} className={PRIMARY_BUTTON_CLASS}>
                See each situation
              </Button>
            </>
          )}
          {phase === "failed" && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const PreRun = ({ copy, genieName, error }: { copy: PhoneCheckCopy; genieName: string; error?: string | null }) => (
  <div className="space-y-6">
    <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
      <div className="rounded-[10px] bg-white p-5">
        <p className="font-semibold text-lg text-gray-900">{SITUATION_COUNT} live situations, no real call</p>
        <p className="text-sm text-gray-600 mt-2 leading-relaxed">{copy.summary(genieName)}</p>
      </div>
    </div>

    <div>
      <p className="text-sm font-semibold text-gray-700 mb-3">What we'll try</p>
      <div className="space-y-2">
        {copy.situations.map((item) => (
          <div key={item.label} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-[#E5EAF0]">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-genie-primary/10 text-xs font-bold text-genie-primary">
              {item.count}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 leading-tight">{item.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.blurb}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    <p className="text-xs text-gray-500">{copy.scopeNote}</p>

    {error && <p className="text-sm text-red-600">{error}</p>}
  </div>
);

const InProgress = ({
  copy,
  isWriting,
  testsComplete,
  totalTests,
  executionPercent,
}: {
  copy: PhoneCheckCopy;
  isWriting: boolean;
  testsComplete: number;
  totalTests: number;
  executionPercent: number;
}) => (
  <div className="space-y-6">
    <PhaseRow
      active={isWriting}
      done={!isWriting}
      title="Writing the situations"
      subtitle={isWriting ? copy.setupLine : `${totalTests} situations written for your Genie`}
      percent={isWriting ? 40 : 100}
    />
    <PhaseRow
      active={!isWriting}
      done={false}
      muted={isWriting}
      title="Playing them against your Genie"
      subtitle={isWriting ? "Starts as soon as the situations are ready" : `${testsComplete} of ${totalTests} conversations complete`}
      percent={isWriting ? 0 : executionPercent}
    />
    <div className="rounded-2xl bg-white border border-[#E5EAF0] p-5 text-sm text-gray-600 leading-relaxed">
      <p className="font-medium text-gray-900 mb-1">This usually takes about a minute.</p>
      You can close this and the result will be waiting in the Phone Tests list.
    </div>
  </div>
);

const CompletedState = ({ copy, score, total }: { copy: PhoneCheckCopy; score: number | null; total: number }) => {
  const passed = score == null ? null : Math.round((score / 100) * total);
  const allRight = passed != null && passed === total;

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center py-4">
        <div className="relative h-40 w-40">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
          <div className="absolute inset-[6px] rounded-full bg-white" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-5xl font-bold text-[#222B45] leading-none">{passed ?? "—"}</p>
            <p className="text-xs uppercase tracking-wider text-[#64748B] mt-1">of {total} right</p>
          </div>
        </div>
        <p className="mt-5 text-lg font-semibold text-[#222B45]">
          {allRight ? copy.allRight : "Some situations need a look"}
        </p>
      </div>

      <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <div className="rounded-[10px] bg-white p-5">
          <p className="text-sm font-semibold text-gray-700 mb-2">What's next</p>
          <p className="text-sm text-gray-600 leading-relaxed">{allRight ? copy.nextAllRight : copy.nextNeedsLook}</p>
        </div>
      </div>
    </div>
  );
};

const FailedState = ({ message, onTryAgain }: { message: string | null; onTryAgain: () => void }) => (
  <div className="space-y-5">
    <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
      <p className="text-sm font-semibold text-red-700 mb-1">The check couldn't run</p>
      <p className="text-sm text-red-600">{message ?? "Something went wrong while running the check."}</p>
    </div>
    <button
      onClick={onTryAgain}
      className="inline-flex items-center gap-2 min-h-[44px] text-sm text-genie-primary hover:underline font-medium"
    >
      <RefreshCw className="h-4 w-4" />
      Try again
    </button>
  </div>
);
